import { useEffect, useRef } from "react";

interface UseInfiniteScrollSentinelArgs {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /**
   * When true, the observer stays disconnected so a failed batch does not
   * auto-retry in a tight loop. The caller surfaces an explicit retry instead.
   */
  isError?: boolean;
}

/**
 * Observes a bottom-of-list sentinel and requests the next batch when it scrolls
 * into view. Guards against duplicate fetches while a batch is already in flight,
 * and stays idle after an error so retries are user-driven, not automatic.
 * Pair the returned ref with the element rendered after the last row.
 */
export function useInfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isError = false,
}: UseInfiniteScrollSentinelArgs) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isError) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "120px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

  return sentinelRef;
}
